// A player's card from any name in a league (B-220, SCENARIOS K6).
//
// ONE CARD FOR EVERY NAME THAT HAS NO SCREEN OF ITS OWN TO OPEN IT. The news,
// a trade, an award, the draft recap: each shows a player in a sentence and
// has his id and his name and nothing else. Each asks this host rather than
// mounting a card of its own. The screens that already open a card from a
// row (the lineup, the matchup, the pool, waivers, the draft room) keep
// theirs, because they hand it what the row knows — this week's points, the
// practice league's action — and a name in a sentence knows none of it.
//
// A REAL LEAGUE'S CARD READS ITSELF (`GET …/players/:playerId`), so an id and
// a name are enough. The practice league has no such read, so the host builds
// the card from the simulator's own player, exactly as its rows do.
//
// THE CARD BELONGS TO THE PAGE IT WAS OPENED ON, as the Edit team sheet does
// (`editTeam.tsx`): the card's own Trade for, or a name in it, leaves the
// page and the card with it, rather than leaving it open underneath.
//
// Mounted inside `/l/:leagueId`, so the card knows its league.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { basisFor, gameStatus, livePoints, playerSummary, projection } from '@fsp/sim';
import { useSim } from '../data/SimProvider';
import { useLeagueSource } from '../data/leagueSource';
import { PlayerCard, type PlayerCardData, type PlayerRef } from '../screens/shared/PlayerCard';
import { useCardAsked } from './findAnything';

type OpenPlayer = (player: PlayerRef) => void;

const PlayerCardContext = createContext<OpenPlayer | null>(null);

interface OpenedOn {
  player: PlayerRef;
  path: string;
}

/** The `/l/:leagueId` layout: every screen in a league, and the one card they share. */
export function PlayerCardHost({ children }: { children?: ReactNode }) {
  const { pathname } = useLocation();
  const [openedOn, setOpenedOn] = useState<OpenedOn | null>(null);

  const open = useCallback<OpenPlayer>(
    (player) => setOpenedOn({ player, path: window.location.pathname }),
    [],
  );
  const close = useCallback(() => setOpenedOn(null), []);
  // A PLAYER FOUND BY NAME (B-402): the finder sits in the frame, outside
  // the league's routes, and asks for his card here.
  useCardAsked(open);

  const stale = openedOn !== null && openedOn.path !== pathname;
  useEffect(() => {
    if (stale) setOpenedOn(null);
  }, [stale]);

  const cardFor = useCardFor();
  const data = useMemo<PlayerCardData | null>(
    () => (openedOn === null || stale ? null : cardFor(openedOn.player)),
    [openedOn, stale, cardFor],
  );

  return (
    <PlayerCardContext.Provider value={open}>
      {children ?? <Outlet />}
      <PlayerCard data={data} onClose={close} />
    </PlayerCardContext.Provider>
  );
}

/** Open a player's card from his name. Inside a league only. */
export function useOpenPlayer(): OpenPlayer {
  const ctx = useContext(PlayerCardContext);
  if (!ctx) throw new Error('useOpenPlayer must be used inside PlayerCardHost');
  return ctx;
}

/**
 * A card from a name alone: the id and the name in a real league, whose card
 * reads the rest itself; the simulator's own player in a practice one, as its
 * rows build theirs. A new function whenever the simulator moves, so a card
 * built from it shows the practice league's minute.
 */
export function useCardFor(): (player: PlayerRef) => PlayerCardData {
  const source = useLeagueSource();
  const { state, version, now } = useSim();
  return useCallback(
    (ref: PlayerRef) => {
      const sim = source === 'sim' ? state.players.get(ref.id) : undefined;
      if (sim === undefined) {
        return { player: ref, game: null, projection: null, points: null, statLine: null };
      }
      const game = gameStatus(state, sim, now);
      return {
        player: playerSummary(state, sim),
        game,
        projection: projection(state, sim),
        basis: basisFor(state, sim, game),
        points: livePoints(state, sim, now),
        statLine: null,
      };
    },
    // `version` is the simulator's change counter; the state object is mutated in place.
    [source, state, version, now],
  );
}

/**
 * EVERY NAME UNDER THIS OPENS ITS CARD HERE, not in the league's shared sheet:
 * My Team's side panel from a tablet up (B-316), so a name in a news line, a
 * claim or a trade opens beside the lineup like a name in the lineup does.
 */
export function PlayerCardsOpenIn({
  open,
  children,
}: {
  open: (player: PlayerRef) => void;
  children: ReactNode;
}) {
  return <PlayerCardContext.Provider value={open}>{children}</PlayerCardContext.Provider>;
}
